/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { AppState } from "Dataflows/Reducers";
import { IAzureBenefitRequest } from "Interfaces/AzureBenefitRequests/IAzureBenefitRequest";
import { IFilterValue } from "Interfaces/IFilterValue";

/**
 * Returns the list of azure benefit requests
 * in the current azure benefit requests state.
 * @param azureBenefitRequestsState
 */
export const getAzureBenefitRequests = (state: AppState): IAzureBenefitRequest[] =>
  state.azureBenefitRequestsState.requests;

/**
 * Returns a boolean value for whether
 * or not the azure benefit requests are still loading.
 * @param azureBenefitRequestsState
 */
export const isLoadingAzureBenefitRequests = (state: AppState): boolean =>
  state.azureBenefitRequestsState.isLoadingAzureBenefitRequests;

/**
 * Returns an error message if there's an
 * issue loading the azure benefit requests.
 * @param visualStudioBenefitState
 */
export const getLoadAzureBenefitRequestsError = (state: AppState): string =>
  state.azureBenefitRequestsState.loadAzureBenefitRequestsError;

/**
 * Returns the current page.
 * @param azureBenefitRequestsState
 */
export const getCurrentPage = (state: AppState): number =>
  state.azureBenefitRequestsState.currentPage;

/**
 * Returns the filtered count.
 * @param azureBenefitRequestsState
 */
export const getFilteredCount = (state: AppState): number =>
  state.azureBenefitRequestsState.filteredCount;

/**
 * Returns the page size.
 * @param azureBenefitRequestsState
 */
export const getPageSize = (state: AppState): number => state.azureBenefitRequestsState.pageSize;

/**
 * Returns the search text.
 * @param azureBenefitRequestsState
 */
export const getSearchText = (state: AppState): string =>
  state.azureBenefitRequestsState.searchText;

/**
 * Returns the list of filter values for
 * azure benefit requests.
 * @param azureBenefitRequestsState
 */
export const getAzureBenefitRequestsFilterValues = (state: AppState): IFilterValue[] =>
  state.azureBenefitRequestsState.azureBenefitRequestsFilterValues;

/**
 * Returns the list of filter values for
 * azure benefit requests.
 * @param azureBenefitRequestsState
 */
export const getSelectedAzureBenefitRequestDetails = (
  state: AppState
): { [key: string]: string } | undefined =>
  state.azureBenefitRequestsState.selectedAzureBenefitRequest;

/**
 * Returns the is expedited value for the selected
 * azure benefit request.
 * @param azureBenefitRequestsState
 */
export const getSelectedAzureBenefitRequestIsExpedited = (state: AppState): boolean =>
  state.azureBenefitRequestsState.selectedAzureBenefitRequestIsExpedited;

/**
 * Returns the expedited by date for the selected
 * azure benefit request.
 * @param azureBenefitRequestsState
 */
export const getSelectedAzureBenefitRequestExpeditedByDate = (state: AppState): Date | undefined =>
  state.azureBenefitRequestsState.selectedAzureBenefitRequestExpeditedByDate;

/**
 * Gets the error message when fetching azure
 * request details fails.
 * @param azureBenefitRequestsState
 */
export const getFetchAzureBenefitRequestDetailsErrorMessage = (state: AppState): string =>
  state.azureBenefitRequestsState.fetchAzureBenefitRequestDetailsErrorMessage;

/**
 * Gets the operation status for approving
 * or declining an azure benefit request.
 * @param azureBenefitRequestsState
 */
export const getApproveOrDeclineRequestOperationStatus = (state: AppState): string =>
  state.azureBenefitRequestsState.azureBenefitApproveOrDeclineOperationStatus;

/**
 * Gets the error message for when approving
 * denying an azure benefit request fails.
 * @param azureBenefitRequestsState
 */
export const getApproveOrDeclineRequestErrorMessage = (state: AppState): string =>
  state.azureBenefitRequestsState.azureBenefitApproveOrDeclineErrorMessage;
